<!--
  Created 01.11.2023
  Transport card component for the proposal show page.
  This component showcases specific transportation entries on the proposal page.
-->
<template>
  <article
    :id="id"
    class="relative flex flex-col overflow-hidden rounded-md border-2 border-aqua-150">
    <!-- Transport heading -->
    <TextEditableTextarea
      v-if="contentEditable"
      id="transport-heading"
      v-model="transportItem.heading"
      background-style="gray"
      class="bg-aqua-150 p-4"
      :text-area-class="'bg-transparent text-center text-sm font-medium text-gray-800 md:text-lg whitespace-pre-wrap ' +
        'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
      :placeholder="'Fill in transport heading..'"
      :show-buttons="false"
      :saved-changes="savedChanges"
      @edited="store.unsavedChanges.changes.proposal = true;" />
    <h1
      v-else
      class="bg-aqua-150 p-4 text-center text-sm font-medium text-gray-800 md:text-lg">
      {{ heading }}
    </h1>
    <TrashIcon
      v-if="contentEditable"
      title="Click to delete this transport"
      class="absolute right-2 top-3 h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-800"
      @click="$emit('removeItem', index)" />
    <!-- Transport content -->
    <div class="flex grow flex-col divide-aqua-150 bg-white max-md:divide-y-2 md:flex-row md:max-lg:divide-x-2 lg:max-xl:flex-col lg:max-xl:divide-y-2 xl:divide-x-2">
      <!-- Transport outbound -->
      <section class="basis-1/2 p-4 text-xs text-gray-700 md:text-sm">
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.outbound.overline"
          :auto-grow="false"
          :tab-rows="1"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-sm text-gray-700 whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in outbound overline..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <h2
          v-else
          class="">
          {{ outbound.overline }}
        </h2>
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.outbound.date"
          :auto-grow="false"
          :tab-rows="1"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-base font-medium text-gray-800 md:text-lg whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in outbound date..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <p
          v-else
          class="text-base font-medium text-gray-800 md:text-lg">
          {{ outbound.date }}
        </p>
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.outbound.departureText"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-sm text-gray-700 whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in departure text..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <p
          v-else
          class="mt-1 whitespace-pre-line">
          {{ outbound.departureText }}
        </p>
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.outbound.arrivalText"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-sm text-gray-700 whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in arrival text..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <p v-else>
          {{ outbound.arrivalText }}
        </p>
      </section>
      <!-- Transport inbound -->
      <section class="basis-1/2 p-4 text-xs text-gray-700 md:text-sm">
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.inbound.overline"
          :auto-grow="false"
          :tab-rows="1"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-sm text-gray-700 whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in inbound overline..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <h2
          v-else
          class="">
          {{ inbound.overline }}
        </h2>
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.inbound.date"
          :auto-grow="false"
          :tab-rows="1"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-base font-medium text-gray-800 md:text-lg whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in inbound date..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <p
          v-else
          class="text-base font-medium text-gray-800 md:text-lg">
          {{ inbound.date }}
        </p>
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.inbound.departureText"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-sm text-gray-700 whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in inbound departure text..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <p
          v-else
          class="mt-1">
          {{ inbound.departureText }}
        </p>
        <TextEditableTextarea
          v-if="contentEditable"
          id="transport-heading"
          v-model="transportItem.inbound.arrivalText"
          background-style="white"
          class=""
          :text-area-class="'bg-transparent text-sm text-gray-700 whitespace-pre-wrap ' +
            'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
          :placeholder="'Fill in inbound arrival text..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <p v-else>
          {{ inbound.arrivalText }}
        </p>
      </section>
    </div>
  </article>
</template>
<script setup>
/* external */
import {
  computed,
} from 'vue';
import { TrashIcon } from '@heroicons/vue/24/outline';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import { useProposalStore } from '@/Stores/Proposal';

/* local */

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

defineEmits(['removeItem']);

const props = defineProps({
  contentEditable: {
    type: Boolean,
    default: false,
  },
  id: {
    type: Number,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  heading: {
    type: String,
    required: true,
    default: 'Gruppe A - Fly, SAS - 50 billetter',
  },
  outbound: {
    type: Object,
    required: true,
    default: () => ({
      overline: 'Utreise',
      date: '10.10.2023',
      departureText: 'Oslo',
      arrivalText: 'Nice',
    }),
    validator(value) {
      return (
        typeof value === 'object'
        && typeof value.overline === 'string'
        && typeof value.date === 'string'
        && typeof value.departureText === 'string'
        && typeof value.arrivalText === 'string'
      );
    },
  },
  inbound: {
    type: Object,
    required: true,
    default: () => ({
      overline: 'Hjemreise',
      date: '10.10.2023',
      departureText: 'Nice',
      arrivalText: 'Oslo',
    }),
    validator(value) {
      return (
        typeof value === 'object'
        && typeof value.overline === 'string'
        && typeof value.date === 'string'
        && typeof value.departureText === 'string'
        && typeof value.arrivalText === 'string'
      );
    },
  },
});

const store = useProposalStore();

const transportItem = computed(() => store.proposal.data.transport.items[props.index]);
const savedChanges = computed(() => !store.unsavedChanges.changes.proposal);

</script>
